import React, { useState } from 'react';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';
import { Button, InlineTextButton, Modal } from '../../components';
import IconClose from '../IconClose/IconClose';

import IconCard from './IconCard/IconCard';
import css from './SavedCardDetails.module.css';

const DEFAULT_CARD = 'defaultCard';
const REPLACE_CARD = 'replaceCard';
const DELETE_CARD_BUTTON_ID = 'deleteCardButton';

/**
 * Saved default card: brand, last4, expiry, plus Replace / Remove.
 * Not a dropdown — Sharetribe stores one default card, so a chevron
 * would imply a list of cards that does not exist.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName]
 * @param {string} [props.className]
 * @param {Object} props.card
 * @param {string} props.card.brand
 * @param {number} props.card.expirationMonth
 * @param {number} props.card.expirationYear
 * @param {string} props.card.last4Digits
 * @param {function} [props.onChange] - 'defaultCard' | 'replaceCard'
 * @param {string} [props.selected] - Controlled 'defaultCard' | 'replaceCard'
 * @param {function} [props.onDeleteCard]
 * @param {function} [props.onManageDisableScrolling]
 * @param {boolean} [props.deletePaymentMethodInProgress]
 * @returns {JSX.Element}
 */
const SavedCardDetails = props => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [active, setActive] = useState(DEFAULT_CARD);
  const intl = useIntl();

  const {
    rootClassName,
    className,
    card,
    onChange,
    selected,
    onDeleteCard,
    onManageDisableScrolling,
    deletePaymentMethodInProgress,
  } = props;
  const isControlled = selected === DEFAULT_CARD || selected === REPLACE_CARD;
  const current = isControlled ? selected : active;

  const { last4Digits, expirationMonth, expirationYear, brand } = card || {};
  const classes = classNames(rootClassName || css.root, className);
  const canDelete = typeof onDeleteCard === 'function';
  const replacing = current === REPLACE_CARD;

  const paymentMethodPlaceholderDesktop = intl.formatMessage(
    { id: 'SavedCardDetails.savedPaymentMethodPlaceholderDesktop' },
    { last4Digits }
  );

  const paymentMethodPlaceholderMobile = intl.formatMessage(
    { id: 'SavedCardDetails.savedPaymentMethodPlaceholderMobile' },
    { last4Digits }
  );

  const isExpired = (month, year) => {
    const currentTime = new Date();
    const currentYear = currentTime.getFullYear();
    const currentMonth = currentTime.getMonth() + 1;
    if (year < currentYear) {
      return true;
    }
    if (year === currentYear && month < currentMonth) {
      return true;
    }
    return false;
  };

  const isCardExpired =
    expirationMonth && expirationYear && isExpired(expirationMonth, expirationYear);

  const handleToggleReplace = e => {
    e.preventDefault();
    e.stopPropagation();
    const next = replacing ? DEFAULT_CARD : REPLACE_CARD;
    if (!isControlled) {
      setActive(next);
    }
    if (onChange) {
      onChange(next);
    }
  };

  const handleDeleteCard = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const removeCardModalTitle = intl.formatMessage({ id: 'SavedCardDetails.removeCardModalTitle' });
  const removeCardModalContent = intl.formatMessage(
    { id: 'SavedCardDetails.removeCardModalContent' },
    { last4Digits }
  );
  const cancel = intl.formatMessage({ id: 'SavedCardDetails.cancel' });
  const removeCard = intl.formatMessage({ id: 'SavedCardDetails.removeCard' });
  const deletePaymentMethod = intl.formatMessage({ id: 'SavedCardDetails.deletePaymentMethod' });
  const replaceCta = intl.formatMessage({
    id: canDelete ? 'SavedCardDetails.replaceCard' : 'SavedCardDetails.useDifferentCard',
  });
  const keepCard = intl.formatMessage({ id: 'SavedCardDetails.keepCard' });

  const showExpired = isCardExpired && !replacing;
  const yearShort =
    expirationYear != null ? expirationYear.toString().substring(2) : '';

  return (
    <div className={classes} data-saved-card="true">
      <div
        className={showExpired ? css.cardRowExpired : css.cardRow}
        data-saved-card-row="true"
      >
        <IconCard brand={brand} className={css.cardIcon} />
        <span className={css.paymentMethodPlaceholderDesktop}>
          {paymentMethodPlaceholderDesktop}
        </span>
        <span className={css.paymentMethodPlaceholderMobile}>{paymentMethodPlaceholderMobile}</span>
        {expirationMonth && expirationYear ? (
          <span className={isCardExpired ? css.expirationDateExpired : css.expirationDate}>
            {expirationMonth}/{yearShort}
          </span>
        ) : null}
      </div>
      {showExpired ? (
        <div className={css.cardExpiredText}>
          {intl.formatMessage({ id: 'SavedCardDetails.expiredCardText' }, { last4Digits })}
        </div>
      ) : null}

      <div className={css.actions}>
        <InlineTextButton
          type="button"
          className={css.replaceButton}
          data-replace-card="true"
          onClick={handleToggleReplace}
        >
          {replacing ? keepCard : replaceCta}
        </InlineTextButton>

        {canDelete && !replacing ? (
          <InlineTextButton
            type="button"
            id={DELETE_CARD_BUTTON_ID}
            onClick={handleDeleteCard}
            className={css.savedPaymentMethodDelete}
            data-remove-card="true"
          >
            <IconClose rootClassName={css.closeIcon} size="small" ariaLabel={deletePaymentMethod} />
            {deletePaymentMethod}
          </InlineTextButton>
        ) : null}
      </div>

      {onManageDisableScrolling ? (
        <Modal
          id="VerifyDeletingPaymentMethod"
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
          usePortal
          contentClassName={css.modalContent}
          onManageDisableScrolling={onManageDisableScrolling}
          focusElementId={DELETE_CARD_BUTTON_ID}
        >
          <div>
            <div className={css.modalTitle}>{removeCardModalTitle}</div>
            <p className={css.modalMessage}>{removeCardModalContent}</p>
            <div className={css.modalButtonsWrapper}>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className={css.cancelCardDelete}
                tabIndex="0"
              >
                {cancel}
              </button>
              <Button onClick={onDeleteCard} inProgress={deletePaymentMethodInProgress}>
                {removeCard}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

export default SavedCardDetails;
